/**
 * The full detail for one monastery event, shown over the monastery page
 * rather than as its own route — the card is a thumbnail, this is the "watch
 * page". Which event is open is held in the URL (`?event=<id>`), so the back
 * button closes it and a pasted link opens straight to it.
 *
 * Structure and behaviour follow DonationConfirmModal: a scrim that closes on
 * click or Escape, the body scroll locked while it is up, focus moved inside.
 */
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Icon from './Icon';
import { formatEventDateRange } from '../lib/format';
import { trackEvent } from '../lib/consent';

/** `YYYY-MM-DD` → `YYYYMMDD`. */
const compact = (ymd) => String(ymd || '').replace(/-/g, '');

/** The day after `ymd`, as `YYYYMMDD` — Google Calendar and iCal treat an
 * all-day event's end date as exclusive. */
const dayAfter = (ymd) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(ymd || ''));
  if (!m) return compact(ymd);
  const date = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3] + 1));
  return date.toISOString().slice(0, 10).replace(/-/g, '');
};

function googleCalendarUrl(event) {
  const start = compact(event.startDate);
  const end = dayAfter(event.endDate || event.startDate);
  const details = [event.description, event.link].filter(Boolean).join('\n\n');
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    dates: `${start}/${end}`,
  });
  if (details) params.set('details', details);
  if (event.locationText) params.set('location', event.locationText);
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/** A minimal VEVENT, as a data: URL the browser saves as an .ics file. */
function icsHref(event, monasteryName) {
  const esc = (s) => String(s || '').replace(/([,;\\])/g, '\\$1').replace(/\n/g, '\\n');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//GOMPA//Events//EN',
    'BEGIN:VEVENT',
    `UID:${event.id}@gompa.app`,
    `DTSTART;VALUE=DATE:${compact(event.startDate)}`,
    `DTEND;VALUE=DATE:${dayAfter(event.endDate || event.startDate)}`,
    `SUMMARY:${esc(event.title)}`,
    `DESCRIPTION:${esc([event.description, event.link].filter(Boolean).join('\n\n'))}`,
    `LOCATION:${esc([event.locationText, monasteryName].filter(Boolean).join(', '))}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ];
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`;
}

const paragraphs = (text) =>
  String(text || '').split(/\n{2,}/).map((s) => s.trim()).filter(Boolean);

export default function EventModal({ event, monasteryName, monasteryId, onClose }) {
  const { t } = useTranslation();
  const closeRef = useRef(null);

  useEffect(() => {
    trackEvent('view_event', { item_id: monasteryId, event_id: event.id, event_name: event.title });
  }, [event.id, monasteryId, event.title]);

  // Scroll-lock, focus handling and focus restoration — once, on mount.
  useEffect(() => {
    const opener = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (opener instanceof HTMLElement) opener.focus();
    };
  }, []);

  // Escape to close — re-bound if `onClose`'s identity changes.
  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const when = formatEventDateRange(event.startDate, event.endDate);
  const body = paragraphs(event.description);

  return (
    <div
      className="event-modal__scrim"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="event-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="event-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          ref={closeRef}
          type="button"
          className="event-modal__close"
          onClick={onClose}
          aria-label={t('common.close', 'Close')}
        >
          <Icon name="close" size={20} />
        </button>

        {event.imageUrl && (
          <div className="event-modal__media">
            <img src={event.imageUrl} alt="" />
          </div>
        )}

        <div className="event-modal__body">
          {event.isPast && (
            <p className="event-modal__past">{t('events.pastLabel', 'Past event')}</p>
          )}
          <h2 id="event-modal-title" className="event-modal__title">{event.title}</h2>

          <dl className="event-modal__facts">
            <div>
              <dt><Icon name="calendar" size={16} /></dt>
              <dd>{when}{event.timeText ? <><br /><span className="subtle">{event.timeText}</span></> : null}</dd>
            </div>
            {event.locationText && (
              <div>
                <dt><Icon name="location" size={16} /></dt>
                <dd>{event.locationText}</dd>
              </div>
            )}
          </dl>

          {body.length > 0 && (
            <div className="event-modal__prose">
              {body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          )}

          <div className="event-modal__actions">
            {!event.isPast && (
              <a
                className="btn btn--primary"
                href={googleCalendarUrl(event)}
                target="_blank"
                rel="noreferrer noopener"
              >
                <Icon name="calendar" size={16} />
                {t('events.addToCalendar', 'Add to calendar')}
              </a>
            )}
            {!event.isPast && (
              <a className="btn btn--ghost" href={icsHref(event, monasteryName)} download={`${event.title}.ics`}>
                {t('events.downloadIcs', 'Download .ics')}
              </a>
            )}
            {event.link && (
              <a
                className={event.isPast ? 'btn btn--primary' : 'btn btn--ghost'}
                href={event.link}
                target="_blank"
                rel="noreferrer noopener"
              >
                {t('events.moreInfo', 'More info')}
                <Icon name="external" size={15} />
              </a>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
